import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import useTitle from '@/hooks/useTitle';
import { useEffect, useState } from 'react';
import tenantService from '@/services/TenantService';
import useFormData from '@/hooks/useFormData';
import useTenantMutation from '@/hooks/useTenantMutation';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@mui/system';
import useTenantQuery from '@/hooks/useTenantQuery';

export default function Organization() {
  useTitle('Verwaltung');

  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  const [tenant, setTenant] = useState(null);

  const { data: roles } = useTenantQuery('role');
  const { data: deployments } = useTenantQuery('deployment');

  const { formData, handleChange, reset, setUpdated } = useFormData(
    tenant || {},
  );

  const [defaultSubscriptions, setDefaultSubscriptions] = useState([]);
  const { mutateAsync: updateTenant } = useTenantMutation(
    'tenant',
    tenant?._id || null,
  );

  useEffect(() => {
    async function fetchTenant() {
      const { data: tenantData } = await tenantService.get('/tenant/admin');
      setTenant(tenantData);
    }
    fetchTenant();
  }, []);

  useEffect(() => {
    if (tenant) {
      reset();
      setDefaultSubscriptions(tenant?.defaultSubscriptions || []);
    }
  }, [tenant]);

  const handleReset = () => {
    reset();
    setDefaultSubscriptions(tenant?.defaultSubscriptions || []);
  };

  const handleSave = async () => {
    const updatedForm = {
      ...formData,
      defaultSubscriptions,
    };
    setTenant(updatedForm);
    await updateTenant(updatedForm);
    setUpdated();
  };

  return (
    <Card sx={{ p: 2, m: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobileView ? 'column' : 'row',
          gap: isMobileView ? 2 : 20,
        }}
      >
        <Box sx={{ display: 'flex', flexFlow: 'column', gap: 1.5, flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Kontaktdaten
          </Typography>
          <TextField
            label="Vorname"
            size="small"
            value={formData?.firstName || ''}
            onChange={handleChange('firstName')}
          />
          <TextField
            label="Nachname"
            size="small"
            value={formData?.lastName || ''}
            onChange={handleChange('lastName')}
          />
          <TextField
            label="E-Mail"
            size="small"
            value={formData?.contactEmail || ''}
            onChange={handleChange('contactEmail')}
          />
          <TextField
            label="Telefon"
            size="small"
            value={formData?.contactPhone || ''}
            onChange={handleChange('contactPhone')}
          />
          <br />
          <TextField
            label="Firma"
            size="small"
            value={formData?.company || ''}
            onChange={handleChange('company')}
          />
          <TextField
            label="Firmenzusatz"
            size="small"
            value={formData?.company2 || ''}
            onChange={handleChange('company2')}
          />
          <TextField
            label="Straße"
            size="small"
            value={formData?.street || ''}
            onChange={handleChange('street')}
          />
          <TextField
            label="Adresszusatz"
            size="small"
            value={formData?.street2 || ''}
            onChange={handleChange('street2')}
          />
          <TextField
            label="Postleitzahl"
            size="small"
            value={formData?.zipcode || ''}
            onChange={handleChange('zipcode')}
          />
          <TextField
            label="Stadt"
            size="small"
            value={formData?.city || ''}
            onChange={handleChange('city')}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            flex: 1,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Zahlungsdetails
          </Typography>

          <TextField
            label="Kontoinhaber"
            size="small"
            value={formData?.accountHolder || ''}
            onChange={handleChange('accountHolder')}
          />
          <TextField
            label="IBAN"
            size="small"
            value={formData?.accountNumber || ''}
            onChange={handleChange('accountNumber')}
          />
          <TextField
            label="BIC"
            size="small"
            value={formData?.bankCode || ''}
            onChange={handleChange('bankCode')}
          />

          <Typography variant="h6" sx={{ my: 2 }}>
            Tenant-Konfiguration
          </Typography>
          <TextField
            label="Microsoft Tenant-ID"
            size="small"
            value={formData?.microsoftTenantID || ''}
            onChange={handleChange('microsoftTenantID')}
          />

          <FormControlLabel
            label="Automatische Registrierung"
            control={
              <Checkbox
                checked={formData.autoSignup === true}
                sx={{ mr: 2 }}
                onChange={handleChange('autoSignup')}
              />
            }
          />

          <TextField
            select
            fullWidth
            label="Standardrolle"
            name="role"
            value={formData.defaultRole || ''}
            variant="outlined"
            size="small"
            onChange={handleChange('defaultRole')}
            InputLabelProps={{ shrink: true }}
          >
            {roles?.map((role) => (
              <MenuItem key={role._id} value={role._id}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>

          <FormControl fullWidth>
            <InputLabel size="small" id="defaultsubscription-chip-label">
              Standard Abonnement
            </InputLabel>
            <Select
              labelId="defaultsubscription-chip-label"
              size="small"
              id="default-subscriptions-chip"
              multiple
              value={defaultSubscriptions}
              onChange={(event) => {
                setDefaultSubscriptions(event.target.value);
              }}
              input={
                <OutlinedInput
                  id="select-multiple-chip"
                  label="Standard Abonnements"
                />
              }
              renderValue={(selectedIds) => {
                if (!deployments) return null;
                const selectedDeps = deployments.filter((dep) =>
                  selectedIds.includes(dep._id),
                );
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedDeps.map((dep) => (
                      <Chip key={dep._id} label={dep.title} />
                    ))}
                  </Box>
                );
              }}
            >
              {deployments.map((deployment) => (
                <MenuItem key={deployment._id} value={deployment._id}>
                  {deployment.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.2, flexFlow: 'row', mt: 4 }}>
        <Button variant="text" onClick={handleReset}>
          Verwerfen
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Speichern
        </Button>
      </Box>
    </Card>
  );
}
